import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Image, Text, TextButton, UpsellBanner } from '@zillow/constellation';
import { CONSTANTS } from '../constants';
import AppIcon from '../images/app-icon.svg';

// need to check if AppIcon is a string or an object; it is different in zillow-rental-manager-web v. zrm-marketing-web
const img = typeof AppIcon === 'string' ? AppIcon : (AppIcon as unknown as { src: string }).src;

/**
 * Returns the upsell href based on user login status and device type.
 *
 * @param {boolean} isLoggedIn - Whether the user is logged in.
 * @param {boolean} isIOS - Whether the user is on an iOS device.
 * @return {string} The upsell href.
 */
export function getUpsellHref(isLoggedIn: boolean, isIOS: boolean): string {
  return isLoggedIn
    ? isIOS
      ? CONSTANTS.HREF_IOS_LOGGEDIN
      : CONSTANTS.HREF_ANDROID_LOGGEDIN
    : isIOS
      ? CONSTANTS.HREF_IOS_LOGGEDOUT
      : CONSTANTS.HREF_ANDROID_LOGGEDOUT;
}

/**
 * A React functional component that renders an upsell banner for the Zillow Rental Manager app.
 * It checks the current path, device, and cookies to determine whether or not to display the banner.
 * If the banner is displayed, it includes a link to download the app for iOS or Android and a close button.
 *
 * @return {JSX.Element|null} The upsell banner component, or null if the banner should not be displayed.
 */
export const AppDownloadUpsellBanner = () => {
  const [browser, setBrowser] = useState<boolean>(false);
  useEffect(() => {
    setBrowser(true);
  }, []);

  if (!browser) {
    return null;
  }

  // Path
  const pathName = window.location.pathname;
  if (!pathName.startsWith('/rental-manager')) {
    return null;
  }

  // User Agent
  const userAgent = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /android/i.test(userAgent);
  const isMobile = isIos || isAndroid;
  if (!isMobile) {
    return null;
  }

  // User
  const userCookie = Cookies.get(CONSTANTS.COOKIE_NAME_USER);
  const isLoggedIn = userCookie !== undefined && userCookie !== null;

  // Cookie
  const cookieName = isLoggedIn ? CONSTANTS.COOKIE_NAME_LOGGEDIN : CONSTANTS.COOKIE_NAME_LOGGEDOUT;
  const cookieExp = isLoggedIn ? CONSTANTS.COOKIE_EXP_LOGGEDIN : CONSTANTS.COOKIE_EXP_LOGGEDOUT;
  const cookieExists = Cookies.get(cookieName);
  if (cookieExists === CONSTANTS.COOKIE_VALUE) {
    return null;
  }

  // Suppress banner if user is on the app
  // check for `hostApp` cookie with value of `ios` or `android`
  const hostAppCookie = Cookies.get(CONSTANTS.COOKIE_NAME_HOSTAPP);
  if (hostAppCookie === CONSTANTS.COOKIE_VALUE_IOS || hostAppCookie === CONSTANTS.COOKIE_VALUE_ANDROID) {
    return null;
  }

  // Upsell
  const upsellText = isLoggedIn ? CONSTANTS.BODY_TEXT_LOGGEDIN : CONSTANTS.BODY_TEXT_LOGGEDOUT;
  const upsellHref = getUpsellHref(isLoggedIn, isIos);

  return (
    <UpsellBanner
      media={<Image src={img} width="48" height="48" alt="Zillow Rental Manager App" />}
      body={<Text>{upsellText}</Text>}
      actionButton={
        <TextButton as="a" href={upsellHref}>
          Open app
        </TextButton>
      }
      onClose={() => {
        Cookies.set(cookieName, CONSTANTS.COOKIE_VALUE, { expires: cookieExp });
      }}
      background="backgroundBlue"
      showBody
    />
  );
};
